import { useModals, Modal, ModalProps, Button, Stack, Switch } from "@skailer-com/uikit";
import useCustomFieldsHook from "@/hooks/use-custom-fields.hook.tsx";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { EmployeeCustomFieldRolesModal } from "@/pages/settings/custom-fields/widgets/EmployeeCustomFieldRolesModal.tsx";
import {
  useFieldDefaultValueDelete,
  useFieldDefaultValueSave,
} from "@/queries/use-custom-fields.query.ts";
import { ApiField } from "@/types/custom-field.types.ts";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

export interface EmployeeCustomFieldDefaultValueProps extends ModalProps {
  field: ApiField;
  fromEdit: boolean;
}

export function EmployeeCustomFieldDefaultValue({
  field,
  fromEdit = true,
  ...props
}: EmployeeCustomFieldDefaultValueProps) {
  const { t } = useTranslation();
  const { id } = props;
  const { openModal, close } = useModals();
  const { transformFormDataToValue } = useCustomFieldsHook();
  const { mutateAsync: save, isPending: isSavePending } = useFieldDefaultValueSave();
  const { mutateAsync: deleteDefaultValue, isPending: isDeletePending } =
    useFieldDefaultValueDelete();
  const { getFieldByType, getSchemaForField, getValueForField } = useCustomFieldsHook();
  const EmployeeCustomFieldDefaultValueSchemaObject = yup.object({
    is_on: yup.boolean().required(),
    ...getSchemaForField(field),
  });

  type EmployeeCustomFieldDefaultValueSchema = yup.InferType<
    typeof EmployeeCustomFieldDefaultValueSchemaObject
  >;

  const formHook = useForm<EmployeeCustomFieldDefaultValueSchema>({
    mode: "onSubmit",
    resolver: yupResolver(EmployeeCustomFieldDefaultValueSchemaObject),
    defaultValues: {
      is_on: !!field.default_value,
      ...getValueForField(field, [], true),
    },
  });

  const {
    watch,
    handleSubmit,
    control,
    formState: { errors },
  } = formHook;

  const withDefault = watch("is_on");

  const onSubmit = handleSubmit(async (data) => {
    if (isSavePending || isDeletePending) return;

    const fieldSaved = await save({
      id: field.id,
      payload: {
        default_value: transformFormDataToValue(field, data),
        upsert_to_all_entities: !fromEdit,
      },
    });

    if (fieldSaved) exit();
  });

  const onNext = async () => {
    if (withDefault) {
      onSubmit().then();
    } else {
      if (!field.default_value) return exit();

      try {
        await deleteDefaultValue(field.id);

        exit();
      } catch (e) {
        console.log(e);
      }
    }
  };

  const exit = () => {
    close(id);

    if (!fromEdit) {
      openModal(EmployeeCustomFieldRolesModal, { field });
    }
  };

  return (
    <Modal
      title={t("custom_fields:default_value")}
      {...props}
      actions={
        <Stack>
          <ServerErrorField errors={errors} />
          <Button size={"lg"} onClick={onNext} isLoading={isSavePending || isDeletePending}>
            {t("common:next")}
          </Button>
        </Stack>
      }
    >
      <form onSubmit={onNext}>
        <Stack gap={"md"}>
          <Controller
            render={({ field: { onChange, value } }) => (
              <Switch
                checked={value}
                onChange={onChange}
                title={t("custom_fields:set_default_value")}
                description={
                  fromEdit
                    ? t("custom_fields:set_default_apply_to_new")
                    : t("custom_fields:set_default_apply_to_all")
                }
              ></Switch>
            )}
            name={"is_on"}
            control={control}
          />
          {withDefault && getFieldByType({ field, formHook, canEdit: true })}
        </Stack>
      </form>
    </Modal>
  );
}
